import { useFormContext } from 'react-hook-form';
import FieldError from '@components/field-error';
import type { IHistoryOption } from '@/types/history/history-demographics';
import type { IHistoryDiagnosedFormValues } from '@/types/history/history-diagnosed';
import type { IHistoryFamilyFormValues } from '@/types/history/history-family';
import type { IHistoryMiscFormValues } from '@/types/history/history-misc';
import type { IHistorySocialFormValues } from '@/types/history/history-social';

type TStringKeys<T> = { [K in keyof T]: T[K] extends string ? K : never }[keyof T];

type TFormValues = Record<string, string>;

type TName =
    | keyof IHistoryDiagnosedFormValues
    | keyof IHistoryFamilyFormValues
    | TStringKeys<IHistoryMiscFormValues>
    | TStringKeys<IHistorySocialFormValues>;

export const RadioField = ({
    name,
    label,
    options,
    help,
}: {
    name: TName;
    label: string;
    options: IHistoryOption[];
    help?: string;
}) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<TFormValues>();

    return (
        <div className="form-group">
            <label className="control-label col-xs-12">
                {label}?{help && <span className="help-block">{help}</span>}
                <FieldError message={errors[name]?.message} />
            </label>
            <div className="col-xs-12">
                {options.map((o) => (
                    <label className="radio-inline" key={o.value}>
                        <input type="radio" value={o.value} {...register(name)} /> {o.label}
                    </label>
                ))}
            </div>
        </div>
    );
};

export const BoolRadioField = ({
    name,
    label,
    yesLabel,
    noLabel,
}: {
    name: TName;
    label: string;
    yesLabel: string;
    noLabel: string;
}) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<TFormValues>();

    return (
        <div className="form-group">
            <label className="control-label col-xs-12">
                {label}?<FieldError message={errors[name]?.message} />
            </label>
            <div className="col-xs-12">
                <label className="radio-inline">
                    <input type="radio" value="1" {...register(name)} /> {yesLabel}
                </label>
                <label className="radio-inline">
                    <input type="radio" value="0" {...register(name)} /> {noLabel}
                </label>
            </div>
        </div>
    );
};

export const SelectField = ({
    name,
    label,
    options,
    placeholder,
}: {
    name: TName;
    label: string;
    options: IHistoryOption[];
    placeholder: string;
}) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<TFormValues>();

    return (
        <div className="form-group">
            <label className="control-label col-xs-12">
                {label}?<FieldError message={errors[name]?.message} />
            </label>
            <div className="col-xs-12">
                <select className="form-control" {...register(name)}>
                    <option value="">{placeholder}</option>
                    {options.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export const NumberSelectField = ({
    name,
    label,
    max,
    plusLast,
    placeholder,
}: {
    name: TName;
    label: string;
    max: number;
    plusLast?: boolean;
    placeholder: string;
}) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<TFormValues>();

    const nums = Array.from({ length: max }, (_, i) => i + 1);

    return (
        <div className="form-group">
            <label className="control-label col-xs-12">
                {label}?<FieldError message={errors[name]?.message} />
            </label>
            <div className="col-xs-12">
                <select className="form-control" {...register(name)}>
                    <option value="">{placeholder}</option>
                    {nums.map((n) => (
                        <option key={n} value={n}>
                            {plusLast && n === max ? `${n}+` : n}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export const TextField = ({
    name,
    label,
    maxLength,
}: {
    name: TName;
    label: string;
    maxLength?: number;
}) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<TFormValues>();

    return (
        <div className="form-group">
            <label className="control-label col-xs-12">
                {label}?<FieldError message={errors[name]?.message} />
            </label>
            <div className="col-xs-12 col-sm-8">
                <input className="form-control" maxLength={maxLength} {...register(name)} />
            </div>
        </div>
    );
};
